import { useState, useEffect, useRef } from 'react';
import {
  Edit3,
  Trash2,
  Copy,
  Scissors,
  Plus,
  FolderPlus,
  History,
  GitCompare,
  Info,
  Download,
  Eye
} from 'lucide-react';
import type { FileType } from '@/routes/chat/hooks/use-chat';

export interface ContextMenuPosition {
  x: number;
  y: number;
}

export interface FileContextMenuProps {
  isOpen: boolean;
  position: ContextMenuPosition;
  targetFile?: FileType;
  targetPath?: string;
  isFolder?: boolean;
  onClose: () => void;
  onNewFile: (parentPath?: string) => void;
  onNewFolder: (parentPath?: string) => void;
  onRename: (file: FileType) => void;
  onDelete: (file: FileType) => void;
  onEdit: (file: FileType) => void;
  onCopy: (file: FileType) => void;
  onCut: (file: FileType) => void;
  onViewHistory: (file: FileType) => void;
  onCompare: (file: FileType) => void;
  onProperties: (file: FileType) => void;
  onDownload: (file: FileType) => void;
  onPreview: (file: FileType) => void;
}

export function FileContextMenu({
  isOpen,
  position,
  targetFile,
  targetPath,
  isFolder = false,
  onClose,
  onNewFile,
  onNewFolder,
  onRename,
  onDelete,
  onEdit,
  onCopy,
  onCut,
  onViewHistory,
  onCompare,
  onProperties,
  onDownload,
  onPreview
}: FileContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  // Adjust position to keep menu within viewport
  useEffect(() => {
    if (!isOpen || !menuRef.current) return;

    const menu = menuRef.current;
    const rect = menu.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let { x, y } = position;

    // Adjust horizontal position
    if (x + rect.width > viewportWidth) {
      x = viewportWidth - rect.width - 10;
    }

    // Adjust vertical position
    if (y + rect.height > viewportHeight) {
      y = viewportHeight - rect.height - 10;
    }

    setAdjustedPosition({ x, y });
  }, [isOpen, position]);

  // Close menu on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleAction = (action: () => void) => {
    action();
    onClose();
  };

  const menuItems = [
    // Creation actions (always available)
    {
      icon: Plus,
      label: 'New File',
      action: () => onNewFile(isFolder ? targetPath : undefined),
      separator: false
    },
    {
      icon: FolderPlus,
      label: 'New Folder',
      action: () => onNewFolder(isFolder ? targetPath : undefined),
      separator: true
    },

    // File-specific actions (only for files)
    ...(targetFile && !isFolder ? [
      {
        icon: Edit3,
        label: 'Edit',
        action: () => onEdit(targetFile),
        separator: false
      },
      {
        icon: Eye,
        label: 'Preview',
        action: () => onPreview(targetFile),
        separator: false
      },
      {
        icon: Download,
        label: 'Download',
        action: () => onDownload(targetFile),
        separator: true
      },
      {
        icon: Copy,
        label: 'Copy',
        action: () => onCopy(targetFile),
        separator: false
      },
      {
        icon: Scissors,
        label: 'Cut',
        action: () => onCut(targetFile),
        separator: true
      },
      {
        icon: History,
        label: 'View History',
        action: () => onViewHistory(targetFile),
        separator: false
      },
      {
        icon: GitCompare,
        label: 'Compare/Diff',
        action: () => onCompare(targetFile),
        separator: true
      }
    ] : []),

    // Common actions (available for both files and folders)
    ...(targetFile ? [
      {
        icon: Edit3,
        label: 'Rename',
        action: () => onRename(targetFile),
        separator: false
      },
      {
        icon: Trash2,
        label: 'Delete',
        action: () => onDelete(targetFile),
        separator: true,
        destructive: true
      },
      {
        icon: Info,
        label: 'Properties',
        action: () => onProperties(targetFile),
        separator: false
      }
    ] : [])
  ];

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-[180px]"
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y
      }}
    >
      {menuItems.map((item, index) => (
        <div key={index}>
          <button
            onClick={() => handleAction(item.action)}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors ${
              item.destructive 
                ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20' 
                : 'text-gray-700 dark:text-gray-300'
            }`}
          >
            <item.icon className="size-4" />
            {item.label}
          </button>
          {item.separator && (
            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Hook for managing context menu state
 */
export function useFileContextMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<ContextMenuPosition>({ x: 0, y: 0 });
  const [targetFile, setTargetFile] = useState<FileType | undefined>();
  const [targetPath, setTargetPath] = useState<string | undefined>();
  const [isFolder, setIsFolder] = useState(false);

  const openMenu = (
    event: React.MouseEvent,
    file?: FileType,
    path?: string,
    folder = false
  ) => {
    event.preventDefault();
    event.stopPropagation();
    
    setPosition({ x: event.clientX, y: event.clientY });
    setTargetFile(file);
    setTargetPath(path);
    setIsFolder(folder);
    setIsOpen(true);
  };

  const closeMenu = () => {
    setIsOpen(false);
    setTargetFile(undefined);
    setTargetPath(undefined);
    setIsFolder(false);
  };

  return {
    isOpen,
    position,
    targetFile,
    targetPath,
    isFolder,
    openMenu,
    closeMenu
  };
}
